/**
 * xlsxPatcher — surgically update cell values inside an existing .xlsx,
 * preserving EVERYTHING else (charts, drawings, images, styles, other
 * sheets) byte-for-byte.
 *
 * Why not ExcelJS: ExcelJS does not round-trip charts/drawings - loading a
 * workbook and writing it back silently drops them. Here we only rewrite the
 * changed cells' XML in the worksheet parts and re-zip every other entry
 * untouched, so charts survive an edit + save.
 */
import JSZip from "jszip";

const MAIN_NS = "http://schemas.openxmlformats.org/spreadsheetml/2006/main";
const R_NS = "http://schemas.openxmlformats.org/officeDocument/2006/relationships";

function colLetter(index) {
  let s = "";
  let n = index;
  for (;;) {
    s = String.fromCharCode(65 + (n % 26)) + s;
    n = Math.floor(n / 26) - 1;
    if (n < 0) break;
  }
  return s;
}
const cellRef = (r, c) => colLetter(c) + (r + 1);
const colIndexFromRef = (ref) => {
  let col = 0;
  for (let i = 0; i < ref.length; i++) {
    const ch = ref.charCodeAt(i);
    if (ch < 65 || ch > 90) break;
    col = col * 26 + (ch - 64);
  }
  return col - 1;
};

// Map each sheet NAME to its worksheet XML path inside the zip.
function buildSheetPathMap(wbDoc, relsDoc) {
  const relMap = {};
  if (relsDoc) {
    const rels = relsDoc.getElementsByTagName("*");
    for (let i = 0; i < rels.length; i++) {
      const el = rels[i];
      if (el.localName !== "Relationship") continue;
      let target = el.getAttribute("Target") || "";
      if (target.charAt(0) === "/") target = target.slice(1);
      else target = "xl/" + target;
      relMap[el.getAttribute("Id")] = target;
    }
  }
  const map = {};
  const sheets = wbDoc.getElementsByTagName("*");
  let idx = 0;
  for (let i = 0; i < sheets.length; i++) {
    const el = sheets[i];
    if (el.localName !== "sheet") continue;
    idx++;
    const name = el.getAttribute("name");
    const rid = el.getAttributeNS(R_NS, "id") || el.getAttribute("r:id");
    map[name] = relMap[rid] || `xl/worksheets/sheet${idx}.xml`;
  }
  return map;
}

// Find or create the <row r="rowNum"> element, inserting in ascending order.
function ensureRow(doc, sheetData, rowNum, ns) {
  let insertBefore = null;
  for (let i = 0; i < sheetData.childNodes.length; i++) {
    const node = sheetData.childNodes[i];
    if (node.nodeType !== 1 || node.localName !== "row") continue;
    const rn = parseInt(node.getAttribute("r"), 10);
    if (rn === rowNum) return node;
    if (rn > rowNum) { insertBefore = node; break; }
  }
  const row = doc.createElementNS(ns, "row");
  row.setAttribute("r", String(rowNum));
  sheetData.insertBefore(row, insertBefore);
  return row;
}

// Find or create the <c r="ref"> element within a row (ascending column).
function ensureCell(doc, row, ref, colIdx, ns) {
  for (let i = 0; i < row.childNodes.length; i++) {
    const node = row.childNodes[i];
    if (node.nodeType !== 1 || node.localName !== "c") continue;
    const cRef = node.getAttribute("r");
    if (cRef === ref) return node;
    if (colIndexFromRef(cRef) > colIdx) {
      const c = doc.createElementNS(ns, "c");
      c.setAttribute("r", ref);
      row.insertBefore(c, node);
      return c;
    }
  }
  const c = doc.createElementNS(ns, "c");
  c.setAttribute("r", ref);
  row.appendChild(c);
  return c;
}

const escapeRegExp = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Rewrite `Old!A1` / `'Old Name'!A1` sheet references inside raw part XML
 * (worksheet formulas, chart series refs, workbook definedNames) so a sheet
 * RENAME doesn't leave dangling references in the saved file — Excel does
 * the same rewrite when a sheet is renamed.
 */
function renameSheetRefsInXml(xml, oldName, newName) {
  let out = xml;
  const needsQuote = /[^A-Za-z0-9_.]/.test(newName) || /^\d/.test(newName);
  const newRef = needsQuote ? `'${String(newName).replace(/'/g, "''")}'` : newName;

  // Quoted form: 'Old Name'! (apostrophes doubled inside).
  const quotedOld = `'${String(oldName).replace(/'/g, "''")}'`;
  out = out.replace(new RegExp(escapeRegExp(quotedOld) + "(?=!)", "g"), newRef);

  // Unquoted form (only exists when the old name needed no quoting):
  // boundary-guarded so "Data!" never matches inside "MyData!".
  if (!/[^A-Za-z0-9_.]/.test(oldName)) {
    out = out.replace(
      new RegExp("(^|[^A-Za-z0-9_.!'])" + escapeRegExp(oldName) + "(?=!)", "g"),
      (_, pre) => pre + newRef
    );
  }
  return out;
}

function clearCell(cell) {
  while (cell.firstChild) cell.removeChild(cell.firstChild);
  cell.removeAttribute("t");
  // keep the style attribute (s) so formatting is preserved.
}

// Apply one edit { value, formula } to a <c> element (preserving its style).
function writeCell(doc, cell, entry, ns) {
  clearCell(cell);
  const { value, formula } = entry;

  if (formula) {
    const f = doc.createElementNS(ns, "f");
    f.textContent = String(formula).replace(/^=/, "");
    cell.appendChild(f);
    if (value !== "" && value != null) {
      const isNum = typeof value === "number" || (!isNaN(Number(value)) && String(value).trim() !== "");
      if (!isNum) cell.setAttribute("t", "str");
      const v = doc.createElementNS(ns, "v");
      v.textContent = String(value);
      cell.appendChild(v);
    }
    return;
  }

  if (value === "" || value === null || value === undefined) return; // empty cell

  const asNum = Number(value);
  const isNumeric = typeof value === "number" || (String(value).trim() !== "" && !isNaN(asNum) && isFinite(asNum));
  if (isNumeric) {
    const v = doc.createElementNS(ns, "v");
    v.textContent = String(value);
    cell.appendChild(v);
  } else {
    cell.setAttribute("t", "inlineStr");
    const is = doc.createElementNS(ns, "is");
    const t = doc.createElementNS(ns, "t");
    if (/^\s|\s$/.test(String(value))) t.setAttribute("xml:space", "preserve");
    t.textContent = String(value);
    is.appendChild(t);
    cell.appendChild(is);
  }
}

/**
 * @param {ArrayBuffer} arrayBuffer  original .xlsx bytes (with charts etc.)
 * @param {Object} editsBySheet      { [sheetName]: { "row:col": { value, formula } } }  (0-based coords)
 * @param {Object} [sheetRenames]    { [originalName]: newName } — applied to workbook.xml
 * @returns {Promise<Uint8Array>}    patched .xlsx bytes, charts preserved
 */
export async function patchWorkbookCells(arrayBuffer, editsBySheet, sheetRenames) {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const parser = new DOMParser();
  const serializer = new XMLSerializer();

  const wbFile = zip.file("xl/workbook.xml");
  if (!wbFile) throw new Error("workbook.xml missing");
  const wbDoc = parser.parseFromString(await wbFile.async("string"), "application/xml");
  const relsFile = zip.file("xl/_rels/workbook.xml.rels");
  const relsDoc = relsFile ? parser.parseFromString(await relsFile.async("string"), "application/xml") : null;
  const sheetPaths = buildSheetPathMap(wbDoc, relsDoc);

  // Apply sheet renames to workbook.xml and alias the worksheet paths under
  // the NEW names so cell edits keyed by the renamed sheet still resolve.
  if (sheetRenames && Object.keys(sheetRenames).length > 0) {
    let wbChanged = false;
    const nodes = wbDoc.getElementsByTagName("*");
    for (let i = 0; i < nodes.length; i++) {
      const el = nodes[i];
      if (el.localName !== "sheet") continue;
      const name = el.getAttribute("name");
      const newName = sheetRenames[name];
      if (newName && newName !== name) {
        if (sheetPaths[name]) sheetPaths[newName] = sheetPaths[name];
        el.setAttribute("name", newName);
        wbChanged = true;
      }
    }
    if (wbChanged) {
      // definedNames in workbook.xml still reference the old sheet name.
      let wbXml = serializer.serializeToString(wbDoc);
      Object.entries(sheetRenames).forEach(([oldN, newN]) => {
        if (oldN !== newN) wbXml = renameSheetRefsInXml(wbXml, oldN, newN);
      });
      zip.file("xl/workbook.xml", wbXml);

      // Rewrite cross-sheet references everywhere formulas/series refs live
      // (worksheet <f> elements, chart <c:f> refs) so the saved workbook has
      // no dangling references to the old name.
      const refParts = Object.keys(zip.files).filter(
        (f) => /^xl\/(worksheets|charts|chartsheets)\/.*\.xml$/i.test(f) && !zip.files[f].dir
      );
      for (const partPath of refParts) {
        // eslint-disable-next-line no-await-in-loop
        const src = await zip.file(partPath).async("string");
        let outXml = src;
        Object.entries(sheetRenames).forEach(([oldN, newN]) => {
          if (oldN !== newN) outXml = renameSheetRefsInXml(outXml, oldN, newN);
        });
        if (outXml !== src) zip.file(partPath, outXml);
      }
    }
  }

  for (const sheetName of Object.keys(editsBySheet)) {
    const cells = editsBySheet[sheetName];
    if (!cells || Object.keys(cells).length === 0) continue;
    const path = sheetPaths[sheetName];
    const file = path && zip.file(path);
    if (!file) continue;

    const doc = parser.parseFromString(await file.async("string"), "application/xml");
    const ns = doc.documentElement.namespaceURI || MAIN_NS;
    let sheetData = doc.getElementsByTagName("sheetData")[0];
    if (!sheetData) {
      sheetData = doc.createElementNS(ns, "sheetData");
      doc.documentElement.appendChild(sheetData);
    }

    Object.keys(cells).forEach((key) => {
      const [r, c] = key.split(":").map(Number);
      const ref = cellRef(r, c);
      const row = ensureRow(doc, sheetData, r + 1, ns);
      const cell = ensureCell(doc, row, ref, c, ns);
      writeCell(doc, cell, cells[key], ns);
    });

    zip.file(path, serializer.serializeToString(doc));
  }

  return zip.generateAsync({ type: "uint8array", compression: "DEFLATE" });
}

export default patchWorkbookCells;
